import { newUuid } from '@/lib/ids';
import type { Uuid } from './db';

export const TAB_SYNC_CHANNEL = 'gentletask.data';

export interface DataChangeMessage {
  type: 'data-changed';
  table: string;
  ids: Uuid[];
  /** 送出訊息的分頁 id，用來略過自己的廣播。 */
  origin: string;
  at: number;
}

interface ChannelLike {
  postMessage: (message: DataChangeMessage) => void;
  addEventListener: (type: string, handler: (event: MessageEvent) => void) => void;
  removeEventListener: (type: string, handler: (event: MessageEvent) => void) => void;
  close: () => void;
}

export interface TabSyncOptions {
  onMessage: (message: DataChangeMessage) => void;
  /** 測試時注入假的 channel。 */
  createChannel?: (name: string) => ChannelLike;
}

function defaultCreateChannel(name: string): ChannelLike {
  return new BroadcastChannel(name) as unknown as ChannelLike;
}

/**
 * 用 BroadcastChannel 通知其他分頁資料已變更（spec 19.2）。
 * 不支援或建立失敗時安靜降級——多分頁同步是加分功能，不能拖垮保存流程。
 */
export function createTabSync(options: TabSyncOptions) {
  const tabId = newUuid();
  const create = options.createChannel ?? defaultCreateChannel;

  let channel: ChannelLike | null = null;
  try {
    channel = create(TAB_SYNC_CHANNEL);
  } catch {
    channel = null;
  }

  const handler = (event: MessageEvent) => {
    const message = event.data as DataChangeMessage | undefined;
    if (!message || message.type !== 'data-changed') return;
    if (message.origin === tabId) return;
    options.onMessage(message);
  };

  channel?.addEventListener('message', handler);

  function publish(input: { table: string; ids: Uuid[] }): void {
    if (!channel) return;
    try {
      channel.postMessage({
        type: 'data-changed',
        table: input.table,
        ids: input.ids,
        origin: tabId,
        at: Date.now(),
      });
    } catch {
      // 廣播失敗不影響本分頁已經寫入的資料。
    }
  }

  function close(): void {
    if (!channel) return;
    channel.removeEventListener('message', handler);
    channel.close();
    channel = null;
  }

  return {
    tabId,
    publish,
    close,
    get isActive() {
      return channel !== null;
    },
  };
}

export type TabSync = ReturnType<typeof createTabSync>;
